import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export interface GrossWeightStateLimit {
  stateAbbreviation: string;
  stateName: string;
  maxGrossWeightPounds: number;
}

export interface GrossWeightPermit {
  id: number;
  stateAbbreviation: string;
  stateName: string;
  maxGrossWeightPounds: number;
  permitNumber: string | null;
  effectiveOn: string | null;
  expiresOn: string;
  isExpired: boolean;
  accountabilityAcknowledgedAt: string;
  createdAt: string;
  createdBy: string;
}

export interface UpsertGrossWeightPermit {
  expiresOn: string;
  effectiveOn: string | null;
  permitNumber: string | null;
  acknowledgedAccountability: boolean;
}

// Overweight permits let a driver run above 80,000 lbs gross, up to the
// state's maximum, without the per-ton overweight deduction (NRG-6028).
@Injectable({
  providedIn: 'root',
})
export class GrossWeightPermitsService {
  constructor(private httpClient: HttpClient) {}

  public async stateLimits(): Promise<GrossWeightStateLimit[]> {
    const results = await this.httpClient
      .get<{ stateLimits: GrossWeightStateLimit[] }>(`${environment.api}/v2/admin/gross_weight_state_limits`)
      .toPromise();
    return results?.stateLimits || [];
  }

  public async driverPermits(driverId: string): Promise<GrossWeightPermit[]> {
    const results = await this.httpClient.get<{ permits: GrossWeightPermit[] }>(this.permitsUrl(driverId)).toPromise();
    return results?.permits || [];
  }

  public async savePermit(
    driverId: string,
    stateAbbreviation: string,
    permit: UpsertGrossWeightPermit,
  ): Promise<GrossWeightPermit> {
    return this.httpClient
      .put<GrossWeightPermit>(`${this.permitsUrl(driverId)}${encodeURIComponent(stateAbbreviation)}`, permit)
      .toPromise();
  }

  public async removePermit(driverId: string, stateAbbreviation: string): Promise<void> {
    await this.httpClient.delete(`${this.permitsUrl(driverId)}${encodeURIComponent(stateAbbreviation)}`).toPromise();
  }

  private permitsUrl(driverId: string): string {
    return `${environment.api}/v2/admin/drivers/${encodeURIComponent(driverId)}/gross_weight_permits/`;
  }
}

// The server answers errors as { error: { message } }. Fall back to a generic
// message rather than showing the carrier a raw HTTP status.
export function permitErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const message = error.error?.error?.message;
    if (typeof message === 'string' && message.length) {
      return message;
    }
    if (error.status === 401 || error.status === 403) {
      return 'You do not have access to manage this driver';
    }
  }
  return fallback;
}
